import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, finalize, forkJoin, merge, Observable, of, Subject, switchMap } from 'rxjs';
import _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { ApiResponse } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { ToastService } from '@app/services/toast.service';
import {
  APPROVAL_ENTITY_TYPES,
  ApprovalEntityType,
  ApprovalPolicy,
  ApprovalPolicyApprover,
  ApprovalPolicyPreview,
  ApprovalPolicyPreviewCommand,
  ApprovalPolicyUpdate,
  ApprovalSlot,
  ApproverType,
} from '../../../models/approval-policy/approval-policy';
import {
  ApprovalPolicyFormControls,
  ApproverFormGroup,
  ApproverFormModel,
  approvalPolicyFieldLabels,
  approverFieldLabels,
  bothLanguagesRequired,
} from '../../../models/approval-policy/approval-policy-form';
import { ApprovalPolicyRoles } from '../../../models/roles/roles.group';
import { Role } from '../../../models/user-manager/roles';
import { User } from '../../../models/user-manager/user';
import { ResponseTranslation, Translation } from '../../../models/translation';
import { ApprovalPolicyProxyService } from '../../../proxies/approval-policy.proxy.service';
import { RolesProxyService } from '../../../proxies/roles.proxy.service';
import { UserProxyService } from '../../../proxies/user.proxy.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';
import { ControlsOf } from '../../../utilities/form.types';

interface PickerOption {
  label: string;
  value: string;
}

interface EntityOption {
  label: string;
  value: ApprovalEntityType;
}

const NEW_ID = 'new';
const LIST_ROUTE = '/approval-policies';

@Component({
  selector: 'app-approval-policy-details',
  templateUrl: './approval-policy-details.component.html',
})
export class ApprovalPolicyDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly approvalPolicyProxyService = inject(ApprovalPolicyProxyService);
  private readonly userProxyService = inject(UserProxyService);
  private readonly rolesProxyService = inject(RolesProxyService);

  readonly canEdit = AuthService.hasRole(ApprovalPolicyRoles.ApprovalPolicyAdmin);
  readonly fieldLabels = approvalPolicyFieldLabels;
  readonly approverLabels = approverFieldLabels;

  isLoading = signal(0);
  isNew = signal(true);
  currentId = signal<string | null>(null);
  headerTitle = signal(this.label('ApprovalPolicies_New', 'New policy'));
  policy = signal<ApprovalPolicy | null>(null);
  preview = signal<ApprovalPolicyPreview | null>(null);
  userOptions = signal<PickerOption[]>([]);
  roleOptions = signal<PickerOption[]>([]);

  menuItems = computed<MenuItem[]>(() => [
    { label: this.label('ApprovalPolicies_Title', 'Approval policies'), route: LIST_ROUTE },
    { label: this.headerTitle() },
  ]);

  saveMenuItems: MenuItem[] = [
    {
      label: this.label('Save and Close', 'Save and Close'),
      icon: 'pi pi-save',
      command: () => this.save(true),
    },
  ];

  readonly approverTypeOptions = [
    { label: this.label('ApprovalPolicies_Type_User', 'User'), value: ApproverType.User },
    { label: this.label('ApprovalPolicies_Type_Role', 'Role'), value: ApproverType.Role },
  ];

  readonly entityOptions: EntityOption[] = APPROVAL_ENTITY_TYPES.map((t) => ({
    label: this.label(t.labelKey, t.fallback),
    value: t.value,
  }));

  readonly approvers = new FormArray<ApproverFormGroup>([]);

  readonly form = new FormGroup<ApprovalPolicyFormControls>({
    name: new FormGroup(
      {
        en: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
        es: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      },
      { validators: bothLanguagesRequired },
    ),
    isActive: new FormControl(true, { nonNullable: true }),
    minimumApprovals: new FormControl(0, { nonNullable: true, validators: [Validators.required, Validators.min(0)] }),
    appliesTo: new FormControl<ApprovalEntityType[]>([], { nonNullable: true, validators: [Validators.required] }),
    defaultEntities: new FormControl<ApprovalEntityType[]>([], { nonNullable: true }),
    approvers: this.approvers,
  });

  /** Shows the Spanish name input, as in the Item master modal. UI state only: both names are always required. */
  readonly spanishTranslate = new FormControl(false, { nonNullable: true });

  /** Simulator amount (USD). Transient UI state, never saved; empty = no simulation. */
  readonly simulatorAmount = new FormControl<number | null>(null);

  /** Mirrors appliesTo, also when it is patched without events, so the default options follow it. */
  private readonly appliesTo = signal<ApprovalEntityType[]>([]);

  /** A policy can only be the default for types it applies to. */
  defaultOptions = computed(() => this.entityOptions.filter((o) => this.appliesTo().includes(o.value)));

  /** A new policy shows its errors only once the user has edited it or tried to save; an existing one always. */
  private readonly edited = signal(false);
  showErrors = computed(() => !this.isNew() || this.edited());

  private readonly userNames = computed(() => new Map(this.userOptions().map((o) => [o.value.toLowerCase(), o.label])));
  private readonly previewTrigger$ = new Subject<void>();

  ngOnInit(): void {
    const id = this.route.snapshot.queryParamMap.get('id');
    if (id && id !== NEW_ID) {
      this.isNew.set(false);
      this.currentId.set(id);
    }

    this.form.controls.appliesTo.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((applies) => {
      this.appliesTo.set(applies);
      const defaults = this.form.controls.defaultEntities.value;
      const kept = defaults.filter((d) => applies.includes(d));
      if (kept.length !== defaults.length) this.form.controls.defaultEntities.setValue(kept);
    });

    merge(this.form.valueChanges, this.simulatorAmount.valueChanges, this.previewTrigger$)
      .pipe(
        debounceTime(400),
        switchMap(() => this.approvalPolicyProxyService.preview(this.previewCommand())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (this.form.dirty) this.edited.set(true);
        this.preview.set(response.data ?? null);
      });

    this.loadItem(this.currentId());
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private loadItem(id: string | null): void {
    const policy$: Observable<ApiResponse<ApprovalPolicy> | null> = _.isNil(id) ? of(null) : this.approvalPolicyProxyService.getById(id);

    this.startLoading();
    forkJoin({ users: this.userProxyService.getAllUsers(), roles: this.rolesProxyService.getAllRoles(), policy: policy$ })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe(({ users, roles, policy }) => {
        this.userOptions.set(this.toUserOptions(users.data ?? []));
        this.roleOptions.set(this.toRoleOptions(roles.data ?? []));
        const loaded = policy?.data;
        if (loaded) this.setPolicy(loaded);
        else this.addApprover();
        if (!this.canEdit) this.form.disable({ emitEvent: false });
        this.previewTrigger$.next();
      });
  }

  private setPolicy(policy: ApprovalPolicy): void {
    this.policy.set(policy);
    this.headerTitle.set(this.localized(policy.name));
    this.approvers.clear({ emitEvent: false });
    policy.approvers.forEach((a) => this.approvers.push(this.approverGroup(a), { emitEvent: false }));
    this.form.patchValue(
      {
        name: { en: policy.name.en, es: policy.name.missingTranslations?.includes('es') ? '' : policy.name.es },
        isActive: policy.isActive,
        minimumApprovals: policy.minimumApprovals,
        appliesTo: policy.appliesTo,
        defaultEntities: policy.defaultEntities,
      },
      { emitEvent: false },
    );
    this.appliesTo.set(policy.appliesTo);
    this.form.markAsPristine();
  }

  // ---- Approvers grid

  private approverGroup(approver?: ApprovalPolicyApprover): ApproverFormGroup {
    return new FormGroup<ControlsOf<ApproverFormModel>>({
      type: new FormControl(approver?.type ?? ApproverType.User, { nonNullable: true }),
      value: new FormControl(approver?.value ?? '', { nonNullable: true, validators: [Validators.required] }),
      order: new FormControl<number | null>(approver?.order ?? null, Validators.min(1)),
      isRequired: new FormControl(approver?.isRequired ?? false, { nonNullable: true }),
      fromAmountUsd: new FormControl<number | null>(approver?.fromAmountUsd ?? null, Validators.min(0)),
    });
  }

  addApprover(): void {
    this.approvers.push(this.approverGroup());
  }

  removeApprover(index: number): void {
    this.approvers.removeAt(index);
    this.approvers.markAsDirty();
  }

  onTypeChange(row: ApproverFormGroup): void {
    row.controls.value.setValue('');
  }

  isRole(row: ApproverFormGroup): boolean {
    return row.controls.type.value === ApproverType.Role;
  }

  // ---- Save

  save(shouldClose = false): void {
    if (this.form.invalid) {
      this.edited.set(true);
      this.form.markAllAsTouched();
      if (this.form.controls.name.controls.es.invalid) this.spanishTranslate.setValue(true);
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate, this.toast);
      return;
    }

    const id = this.currentId();
    const body = this.toUpdate();
    const save$ =
      this.isNew() || _.isNil(id) ? this.approvalPolicyProxyService.create(body) : this.approvalPolicyProxyService.update(id, body);

    this.startLoading();
    save$.pipe(finalize(() => this.stopLoading())).subscribe((response) => {
      const saved = response.data;
      if (_.isNil(saved)) return;
      this.toast.success(this.label('Common_Success', 'Success'), this.label('ApprovalPolicies_Saved', 'Approval policy saved.'));
      if (shouldClose) {
        this.back();
        return;
      }
      if (this.isNew()) {
        this.isNew.set(false);
        this.currentId.set(saved.id);
        this.router.navigate([], { queryParams: { id: saved.id }, replaceUrl: true });
      }
      this.setPolicy(saved);
      this.previewTrigger$.next();
    });
  }

  back(): void {
    this.router.navigate([LIST_ROUTE]);
  }

  // ---- Preview / simulator display

  localized(message: ResponseTranslation | null | undefined): string {
    return this.localeHelper.getLocalizedValue(message);
  }

  entityLabel(code: ApprovalEntityType): string {
    return this.entityOptions.find((o) => o.value === code)?.label ?? String(code);
  }

  slotLabel(slot: ApprovalSlot): string {
    return slot.type === ApproverType.Role
      ? `${this.label('ApprovalPolicies_Type_Role', 'Role')} ${slot.value}`
      : (this.userNames().get(slot.value.toLowerCase()) ?? slot.value);
  }

  private previewCommand(): ApprovalPolicyPreviewCommand {
    return {
      id: this.currentId(),
      policy: this.toUpdate(),
      usdAmount: this.simulatorAmount.value,
      requesterUsername: null,
    };
  }

  /** The form as the API's body. A cleared number input writes null; the API's neutral value replaces it. */
  private toUpdate(): ApprovalPolicyUpdate {
    const raw = this.form.getRawValue();
    return {
      name: this.toName(raw.name),
      isActive: raw.isActive,
      minimumApprovals: raw.minimumApprovals ?? 0,
      appliesTo: raw.appliesTo,
      defaultEntities: raw.defaultEntities,
      approvers: raw.approvers.map((a) => ({
        type: a.type,
        value: a.value ?? '',
        order: a.order,
        isRequired: a.isRequired,
        fromAmountUsd: a.fromAmountUsd,
      })),
    };
  }

  /** The Translation converter rejects a blank English name, so a preview of an unnamed form sends none. */
  private toName(name: Translation): Translation | null {
    const en = name.en.trim();
    return en === '' ? null : { en, es: name.es.trim() };
  }

  private toUserOptions(users: User[]): PickerOption[] {
    return users
      .flatMap((u) => {
        const username = u.username;
        if (_.isNil(username) || username === '') return [];
        const fullName = [u.profile?.firstName, u.profile?.lastName].filter((n) => !_.isNil(n) && n !== '').join(' ');
        return [{ value: username, label: fullName ? `${username} · ${fullName}` : username }];
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private toRoleOptions(roles: Role[]): PickerOption[] {
    const names = [...new Set(roles.flatMap((r) => (_.isNil(r.name) || r.name === '' ? [] : [r.name])))];
    return names.sort((a, b) => a.localeCompare(b)).map((name) => ({ value: name, label: name }));
  }

  private label(key: string, fallback: string): string {
    const translation = this.translate.instant(key);
    return translation === key ? fallback : translation;
  }
}
